/** Describe the element under the cursor and build the agent handoff brief. */

const CHIP_LABELS = {
  change_text: "Change text",
  restyle: "Restyle",
  move: "Move",
  remove: "Remove",
  add: "Add something here",
  bug: "Bug",
};

export const EDIT_CHIPS = Object.entries(CHIP_LABELS);

export const PRODUCT_LABELS = {
  lincwerks: "LincWerks",
  focuslinc: "FocusLinc",
  forgelinc: "ForgeLinc",
  gearlinc: "GearLinc",
  commandlinc: "CommandLinc",
  buildlinc: "BuildLinc",
  businesses: "Businesses",
  app: "App",
};

function pathOnly(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  return text.split("?")[0].split("#")[0];
}

/** Product key for a captured page. Keep in step with backend product_from_route. */
export function productFromRoute(route, openTarget = "") {
  const raw = String(route || "").trim();
  const query = raw.includes("?") ? raw.slice(raw.indexOf("?") + 1) : "";
  let path = pathOnly(raw) || "/";
  if (!path.startsWith("/")) path = `/${path}`;
  const opened = new URLSearchParams(query).get("open") || openTarget || "";
  if ((path === "/" || path === "") && String(opened).startsWith("/")) {
    path = pathOnly(opened) || path;
  }
  if (path === "/focus" || path === "/focus/redesign") return "lincwerks";
  if (path.startsWith("/focus/")) return "focuslinc";
  if (path.startsWith("/linkforge") || path.startsWith("/lincforge") || path.startsWith("/forgelinc")) return "forgelinc";
  if (path.startsWith("/gearlinc")) return "gearlinc";
  if (path.startsWith("/commandlinc/buildlinc")) return "buildlinc";
  if (
    path.startsWith("/commandlinc")
    || path === "/dashboard"
    || path.startsWith("/missions")
    || path.startsWith("/projects/")
  ) return "commandlinc";
  if (path.startsWith("/businesses")) return "businesses";
  return "app";
}

export function textSnippet(node, limit = 80) {
  const raw = String(node?.innerText || node?.textContent || "").replace(/\s+/g, " ").trim();
  if (!raw) return "";
  if (raw.length <= limit) return raw;
  return `${raw.slice(0, Math.max(1, limit - 1))}…`;
}

export function friendlyName(element) {
  if (!element || element.nodeType !== 1) return "element";
  const named = element.closest?.("[data-edit-name]");
  if (named) {
    const name = String(named.getAttribute("data-edit-name") || "").trim();
    if (name) {
      if (named === element) return name;
      const snippet = textSnippet(element, 32);
      return snippet ? `${name} · ${snippet}` : name;
    }
  }
  const tested = element.closest?.("[data-testid]");
  if (tested) {
    const id = String(tested.getAttribute("data-testid") || "").trim();
    if (id && tested === element) return id;
  }
  const tag = String(element.tagName || "element").toLowerCase();
  const snippet = textSnippet(element, 42);
  return snippet ? `${tag} “${snippet}”` : tag;
}

export function dataAttributes(element) {
  const out = {};
  const attrs = element?.attributes;
  if (!attrs) return out;
  for (let i = 0; i < attrs.length; i += 1) {
    const attr = attrs[i];
    if (!attr.name?.startsWith("data-")) continue;
    out[attr.name] = String(attr.value || "").slice(0, 180);
    if (Object.keys(out).length >= 40) break;
  }
  return out;
}

function escapeIdent(value) {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") return CSS.escape(value);
  return String(value).replace(/[^A-Za-z0-9_-]/g, "\\$&");
}

export function cssPath(element) {
  const parts = [];
  let node = element;
  while (node && node.nodeType === 1 && node !== document.body && parts.length < 8) {
    const tag = node.tagName.toLowerCase();
    if (node.id && /^[A-Za-z][\w-]*$/.test(node.id)) {
      parts.unshift(`${tag}#${escapeIdent(node.id)}`);
      break;
    }
    const testId = node.getAttribute("data-testid");
    if (testId) {
      parts.unshift(`${tag}[data-testid="${String(testId).replace(/"/g, "")}"]`);
      break;
    }
    const editName = node.getAttribute("data-edit-name");
    if (editName) {
      parts.unshift(`${tag}[data-edit-name="${String(editName).replace(/"/g, "")}"]`);
      break;
    }
    let part = tag;
    const parent = node.parentElement;
    if (parent) {
      const same = Array.from(parent.children).filter((child) => child.tagName === node.tagName);
      if (same.length > 1) part += `:nth-of-type(${same.indexOf(node) + 1})`;
    }
    parts.unshift(part);
    node = node.parentElement;
  }
  return parts.join(" > ");
}

export function reactComponentChain(element) {
  if (!element) return [];
  const key = Object.keys(element).find((name) => name.startsWith("__reactFiber$") || name.startsWith("__reactInternalInstance$"));
  if (!key) return [];
  const names = [];
  let fiber = element[key];
  let guard = 0;
  while (fiber && guard < 30 && names.length < 8) {
    const type = fiber.type;
    let name = "";
    if (typeof type === "function" || (type && typeof type === "object")) {
      name = type.displayName || type.name || "";
    }
    if (name && name !== names[names.length - 1]) names.push(name);
    fiber = fiber.return;
    guard += 1;
  }
  return names;
}

export function boundingBox(element) {
  const rect = element.getBoundingClientRect();
  return {
    x: Math.round(rect.x),
    y: Math.round(rect.y),
    width: Math.round(rect.width),
    height: Math.round(rect.height),
  };
}

export function climbToEditable(element) {
  if (!element || element.nodeType !== 1) return null;
  if (element.closest?.("[data-edit-chrome]")) return null;
  const svg = element.closest?.("svg");
  if (svg) {
    const host = svg.closest("button, a, [data-edit-name], [data-testid]") || svg.parentElement;
    if (host && host !== document.body && host !== document.documentElement) return host;
  }
  if (element === document.body || element === document.documentElement) return null;
  return element;
}

export function describeElement(element, extra = {}) {
  const attrs = dataAttributes(element);
  return {
    label: friendlyName(element),
    tag: String(element.tagName || "").toLowerCase(),
    selector: cssPath(element),
    test_id: element.getAttribute?.("data-testid") || null,
    edit_name: element.getAttribute?.("data-edit-name")
      || element.closest?.("[data-edit-name]")?.getAttribute("data-edit-name")
      || null,
    data_attributes: attrs,
    component_chain: reactComponentChain(element),
    text_snippet: textSnippet(element, 180),
    bounding_box: boundingBox(element),
    region: extra.region || null,
  };
}

export function elementsInRect(rect, root = document.body) {
  if (!rect || !root?.querySelectorAll) return [];
  const hits = [];
  const nodes = root.querySelectorAll("button, a, section, article, header, form, nav, li, [data-edit-name], [data-testid]");
  nodes.forEach((element) => {
    if (element.closest?.("[data-edit-chrome]")) return;
    const box = element.getBoundingClientRect();
    if (box.width < 8 || box.height < 8) return;
    const centerX = box.left + box.width / 2;
    const centerY = box.top + box.height / 2;
    const centerIn = centerX >= rect.x && centerX <= rect.x + rect.width && centerY >= rect.y && centerY <= rect.y + rect.height;
    if (!centerIn) return;
    hits.push(element);
  });
  const pruned = hits.filter((element) => !hits.some((other) => other !== element && element.contains(other)));
  return pruned.slice(0, 12);
}

export function pageContext() {
  const params = new URLSearchParams(window.location.search);
  const openTarget = params.get("open");
  const route = `${window.location.pathname}${window.location.search}`;
  return {
    route,
    url: window.location.href,
    open_target: openTarget || null,
    page_title: document.title || "",
    viewport: { width: window.innerWidth, height: window.innerHeight },
    product: productFromRoute(route, openTarget || ""),
  };
}

export function buildEditRequestPayload({ text, chip, selections, shots }) {
  return {
    ...pageContext(),
    request_text: String(text || "").trim(),
    chip: chip || null,
    selections,
    screenshot_base64: shots?.viewport_jpeg_base64 || null,
    screenshot_mime: shots?.viewport_mime || null,
    crop_base64: shots?.crop_jpeg_base64 || null,
    crop_mime: shots?.crop_mime || null,
    screenshot_note: shots?.screenshot_note || null,
  };
}

function assetLink(path, origin) {
  if (!path) return "not captured";
  if (/^https?:\/\//i.test(path)) return path;
  const base = String(origin || "").replace(/\/$/, "");
  return `${base}${path}`;
}

export function buildAgentBrief(record, { assetOrigin = "" } = {}) {
  const selections = Array.isArray(record?.selections) ? record.selections : [];
  const lines = [
    `# Edit request ${record?.id || ""}`.trim(),
    "",
    `- Status: ${record?.status || "new"}`,
    `- Product: ${PRODUCT_LABELS[record?.product] || record?.product || "unknown"}`,
    `- Product key: ${record?.product || "unknown"}`,
    `- Page: ${record?.page_title || ""}`,
    `- Route: ${record?.route || ""}`,
    `- URL: ${record?.url || ""}`,
    `- Open target: ${record?.open_target || "none"}`,
    `- Viewport: ${record?.viewport?.width || "?"}×${record?.viewport?.height || "?"}`,
    `- Chip: ${CHIP_LABELS[record?.chip] || record?.chip || "none"}`,
    `- From: ${record?.created_by_name || record?.created_by_email || record?.created_by || ""}`,
    "",
    "## Request",
    "",
    String(record?.request_text || "").trim(),
    "",
    "## Selections",
    "",
  ];
  if (!selections.length) lines.push("No element was captured.");
  selections.forEach((item, index) => {
    const box = item?.bounding_box || {};
    lines.push(`### ${index + 1}. ${item?.label || item?.tag || "element"}`);
    lines.push("");
    lines.push(`- Selector: \`${item?.selector || ""}\``);
    lines.push(`- Tag: ${item?.tag || ""}`);
    lines.push(`- data-testid: ${item?.test_id || "none"}`);
    lines.push(`- data-edit-name: ${item?.edit_name || "none"}`);
    const data = item?.data_attributes || {};
    const dataLine = Object.entries(data).map(([key, value]) => `${key}=${value}`).join("; ");
    lines.push(`- Data attributes: ${dataLine || "none"}`);
    lines.push(`- React component chain: ${(item?.component_chain || []).join(" → ") || "not available"}`);
    lines.push(`- Text: ${item?.text_snippet || ""}`);
    lines.push(`- Bounding box: x ${box.x ?? "?"}, y ${box.y ?? "?"}, ${box.width ?? "?"}×${box.height ?? "?"}`);
    if (item?.region) {
      lines.push(`- Highlight region: x ${item.region.x}, y ${item.region.y}, ${item.region.width}×${item.region.height}`);
    }
    lines.push("");
  });
  lines.push("## Screenshots");
  lines.push("");
  lines.push(`- Viewport: ${assetLink(record?.screenshot_path, assetOrigin)}`);
  lines.push(`- Close-up: ${assetLink(record?.crop_path, assetOrigin)}`);
  if (record?.screenshot_storage_key) lines.push(`- Viewport file: \`backend/data/workspace_uploads/${record.screenshot_storage_key}\``);
  if (record?.crop_storage_key) lines.push(`- Close-up file: \`backend/data/workspace_uploads/${record.crop_storage_key}\``);
  if (record?.screenshot_note) lines.push(`- Note: ${record.screenshot_note}`);
  lines.push("");
  lines.push("## Follow-up");
  lines.push("");
  lines.push(`- Status note: ${record?.note || "none"}`);
  lines.push(`- PR: ${record?.pr_link || "none"}`);
  lines.push("");
  return lines.join("\n");
}
